import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import { subscriptionAPI, accountAPI } from '../../../../../services/redwolfSdkAPI';
import * as Sentry from '@sentry/react';
import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';
import useMobileView from '../../../../../components/common/hooks/useMobileView';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import PlansSkeleton from '../../../../common/skeletons/PlansSkeleton';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs';
import { Accordion, AccordionItem, AccordionButton, AccordionPanel } from '@reach/accordion';

import { useSelector } from 'react-redux';
import { SocialResponse } from '../../../../common/interfaces/user';
import { openNotification } from '../../../../../store/commonSlice';

import { checkEmailIdExits, getUserConsentStatus } from '../../../../../utils/accessRedirection';
import { appConfig } from '../../../../../utils/appConfig';
import { useRecaptcha } from '../../../../common/hooks/useRecaptcha';
import { useDispatch } from 'react-redux';
import { addClasses } from '../../../../../utils/eventListeners';
import { setTermsOfServiceStatus } from '../../../../../store/userSlice';

import SignInGoogle from '../../../../SignIn/SignInGoogle';
import FacebookSignIn from '../../../../SignIn/SignInFacebook';
import SignInApple from '../../../../SignIn/SignInApple';
import useLogout from '../../../../common/hooks/useLogout';

interface SubscriptionRenewModalProps {
	isRenewModalOpen: boolean;
	isSkeletonLoading: boolean;
	plansList: any;
	subscriptionDetail: any;
	renewSubscriptionModalToggle: (
		isRenewSubscriptionModalOpen: boolean,
		emptyPlansList: any,
		reRenderSubscriptionList: any
	) => void;
	updatePurchaseHistory: () => void;
}

const RenewSubscription = ({
	isRenewModalOpen,
	isSkeletonLoading,
	plansList,
	subscriptionDetail,
	renewSubscriptionModalToggle,
	updatePurchaseHistory
}: SubscriptionRenewModalProps) => {
	const [handleUserLogout] = useLogout();
	const [isMobileView] = useMobileView();
	const [isRenewConfirmModalOpen, setIsRenewConfirmModalOpen] = useState(false);
	const [planDetails, setPlanDetails] = useState<any>({});
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [addOnsDetails, setAddOnsDetails]: any = useState([]);
	const [total, setTotal] = useState(0);
	const [freeTrialTotal, setFreeTrialTotal] = useState(0);
	const [addOnsCheckedState, setAddOnsCheckedState] = useState(
		new Array(addOnsDetails.length).fill(false)
	);
	const [addOnsCheckedNames, setAddOnsCheckedNames]: any = useState({ addOnsPlus: [] });
	const [addOnCodesSelected, setAddOnCodesSelected]: any = useState({ addOnCodesPlus: [] });
	const [partnerId, setPlanPartnerId] = useState(null);
	const [reAuthentication, setIsReAuthenticationModalOpen] = useState(false);
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();
	const [socialButtonDisabled, setIsSocialButtonDisabled] = useState(false);
	const [addOnsPlanPartnerId, setAddOnsPlanPartnerId] = useState(null);
	const [dueToday, setDueToday] = useState(false);
	const [planPreviewSubscription, setPlanPreviewSubscription]: any = useState([]);

	const [signInForm, setSignInForm] = useState({ email: '', password: '' });
	const [formErrors, setFormErrorMessage] = useState({
		isEmailFieldValid: true,
		isEmailValidFormt: true,
		emailErrorMessage: '',
		isPasswordFieldValid: true,
		passwordErrorMessage: ''
	});
	const [isSignButtonDisabled, setIsSignButtonDisabled] = useState(false);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isAccountLocked, setIsAccountLocked] = useState(false);
	const [isEmailChanged, setIsEmailChanged] = useState(true);
	const [isUnableFindEmail, setIsUnableFindEmail] = useState(false);
	const [tabIndex, setTabIndex] = useState(0);
	const passwordRef = useRef<HTMLInputElement>(null);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryStrings = new URLSearchParams(useLocation().search);
	const redirectURLFromQueryString = queryStrings.get('redirect_url');

	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();

	const token = getToken();
	const {
		account: { user },
		userProfile
	} = useSelector((state: any) => state.user);

	const transaction = Sentry.startInactiveSpan({ name: 'Resume Subscription' });

	useEffect(() => {
		addClasses();
		if (passwordRef.current) {
			passwordRef.current.focus();
		}
	}, []);

	if (passwordRef.current && passwordRef.current.value) {
		passwordRef.current.classList.add('active');
	}

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	useEffect(() => {
		if (plansList.length) {
			const selectedPlan = plansList.find((plan: any) => plan.selected);
			if (selectedPlan) {
				setPlanDetails(selectedPlan);
				choosePlan(selectedPlan);
			}
		}
	}, [plansList]);

	const handleRenewSubscription = (event: any) => {
		event.preventDefault();
		setIsRenewConfirmModalOpen(true);
	};

	const addonsEqual = (expected: any, actual: any) =>
		actual.length === expected.length &&
		!expected.some(({ code, price }: { code: any; price: any }, i: any) =>
			(({ code: otherName, price: otherPrice }) => code !== otherName || price !== otherPrice)(
				actual[i]
			)
		);

	const handlePreviewSubscription = (planCode: any, addOns: any) => {
		setIsSpinnerLoading(true);
		const addOnCodesList: any = [];
		addOns.map(function (item: any) {
			if (item.checked === true) {
				addOnCodesList.push(item.code);
			}
		});
		const addOnCodes: any = addOnCodesList;
		const couponCode: any = null;

		requestRedwolfService(
			subscriptionAPI.previewSubscription(token, planCode, addOnCodes, couponCode)
		)
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					setIsSpinnerLoading(false);
					let planPreviewSubscription: any = [];
					if (response && response.data) {
						planPreviewSubscription = response.data ? response.data : [];
					}
					setPlanPreviewSubscription(planPreviewSubscription);
				} else {
					setIsSpinnerLoading(false);

					if (response.response.status !== 200) {
						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: response.response.detail
							})
						);
					}
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleRenewSubscriptionConfirm = () => {
		setIsSpinnerLoading(true);
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_CONFIRMATION');

		const addOnCodesList: any = [];
		addOnsDetails.map(function (item: any) {
			if (item.checked === true) {
				addOnCodesList.push(item.code);
			}
		});
		const addOnCodes: any = addOnCodesList;
		const couponCode: any = null;
		const partnerAccountId: any = partnerId;
		const noFreeTrial: any = false;
		const addonsButtonCheck = addonsEqual(defaultAddOns, filterAddOnsName);
		requestRedwolfService(
			subscriptionAPI.reactivateSubscription(token, subscriptionDetail.subscriptionId)
		)
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: 'Subscription resumed.'
						})
					);
					if (
						(subscriptionDetail.plan.planCode === planDetails.planCode && !addonsButtonCheck) ||
						(subscriptionDetail.plan.planCode !== planDetails.planCode && !addonsButtonCheck)
					) {
						requestRedwolfService(
							subscriptionAPI.purchaseSubscription(
								token,
								planDetails.planCode,
								addOnCodes,
								couponCode,
								noFreeTrial,
								partnerAccountId
							)
						)
							.then((purchaseSubscriptionResponse: any) => {
								setIsSpinnerLoading(false);
								if (purchaseSubscriptionResponse.constructor.$metadata$.simpleName === 'Success') {
									logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_SUCCESS');

									setIsRenewConfirmModalOpen(false);
									updatePurchaseHistory();
									renewSubscriptionModalToggle(false, true, true);
									dispatch(
										openNotification({
											type: 'SUCCESS',
											heading: 'Success',
											body: 'Subscription resumed.'
										})
									);
								} else {
									Sentry.captureException(new Error(purchaseSubscriptionResponse));
								}
							})
							.catch((err: any) => {
								Sentry.captureException(err);
								setIsSpinnerLoading(false);
							});
					} else {
						logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_SUCCESS');
						setIsSpinnerLoading(false);
						setIsRenewConfirmModalOpen(false);
						updatePurchaseHistory();
						renewSubscriptionModalToggle(false, true, true);
					}
					renewSubscriptionModalToggle(false, true, true);
					setIsReAuthenticationModalOpen(false);
					navigate('/my-subscriptions');
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_FAILURE', {
						error: 'Unable to process your request. Please try again.'
					});
					setIsSpinnerLoading(false);

					if (response.response.status !== 200) {
						dispatch(
							openNotification({
								type: 'ERROR',
								heading: 'Error',
								body: response.response.detail
							})
						);
					}
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				transaction && transaction.end();

				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_RENEW_SUBSCRIPTION_FAILURE', {
					error: 'Unable to process your request. Please try again.'
				});
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleRenewBack = (event: any) => {
		event.preventDefault();
		setIsRenewConfirmModalOpen(false);
		if (addOnsDetails.length > 0) {
			handleTabsChange(1);
		}
	};

	const renewConfirmModalClose = () => {
		setIsRenewConfirmModalOpen(false);
		renewSubscriptionModalToggle(false, true, null);
	};

	const renewModalClose = () => {
		renewSubscriptionModalToggle(false, true, null);
	};

	const choosePlan = (plan: any) => {
		setIsSpinnerLoading(true);
		const modifiedData = {
			appName: plan.appName,
			billingDate: plan.billingDate,
			billingInterval: plan.billingInterval,
			imageUrl: plan.imageUrl,
			name: plan.name,
			planCode: plan.planCode,
			price: plan.price,
			selected: true
		};
		setPlanDetails(modifiedData);
		setAddOnsCheckedNames({ addOnsPlus: [] });

		setTotal(0);
		setFreeTrialTotal(0);

		requestRedwolfService(subscriptionAPI.getPlan(plan.planCode))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					setIsSpinnerLoading(false);
					let addOns: any = [];
					if (response && response.data) {
						addOns = response.data.addOns ? response.data.addOns.toArray() : [];
					}
					setPlanPartnerId(response.data.partnerId);
					setAddOnsPlanPartnerId(response.data.planCode);
					const addOnsList = addOns.map((v: any) => ({
						...v,
						checked: defaultAddOns.some(function (value: any) {
							if (subscriptionDetail.plan.planCode === response.data.planCode) {
								return value.code == v.code;
							}
						}),
						freeTrialExpired: defaultAddOns.some(function (value: any) {
							if (
								subscriptionDetail.plan.planCode === response.data.planCode &&
								value.code === v.code &&
								v.freeTrialDays > 0
							) {
								return value.code == v.code;
							}
						})
					}));
					if (plan.planCode !== subscriptionDetail.plan.planCode) {
						setDueToday(true);
						handlePreviewSubscription(plan.planCode, []);
					} else {
						setDueToday(false);
						setPlanPreviewSubscription([]);
					}
					setAddOnsDetails(addOnsList);
					setAddOnsCheckedState(new Array(addOns.length).fill(false));
					let total = 0;
					let freeTrialAmount = 0;
					if (addOnsList.length === 0) {
						setAddOnCodesSelected({ addOnCodesPlus: [] });
					}
					addOnsList.forEach((action: any) => {
						if (action.checked === true) {
							const { addOnsPlus } = addOnsCheckedNames;
							const { addOnCodesPlus } = addOnCodesSelected;
							setAddOnsCheckedNames({
								addOnsPlus: [...addOnsPlus, action.name]
							});
							setAddOnCodesSelected({
								addOnCodesPlus: [...addOnCodesPlus, action]
							});
							if (action.freeTrialDays !== 0) {
								freeTrialAmount += parseFloat(action.price.substring(1).replace(/,/g, ''));
							}
							total += parseFloat(action.price.substring(1).replace(/,/g, ''));
						}
					});
					setFreeTrialTotal(freeTrialAmount);
					setTotal(total);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	if (plansList && subscriptionDetail && subscriptionDetail.plan && isRenewModalOpen) {
		const isPlanExists = plansList.find(
			(plan: any) => plan.planCode === subscriptionDetail.plan.planCode
		);
		if (!isPlanExists) {
			plansList.push(subscriptionDetail.plan);
		}
	}
	const handleOnChangeAddOns = (event: any, position: any) => {
		const { value, checked } = event.target;
		const { addOnsPlus } = addOnsCheckedNames;
		const { addOnCodesPlus } = addOnCodesSelected;

		if (checked) {
			setAddOnsCheckedNames({
				addOnsPlus: [...addOnsPlus, value]
			});
			setAddOnCodesSelected({
				addOnCodesPlus: [...addOnCodesPlus, addOnsDetails[position]]
			});
			const addOnsDetailsArray = [...addOnsDetails];
			addOnsDetailsArray[position]['checked'] = checked;
			addOnsDetailsArray[position]['freeTrialExpired'] = false;
			setAddOnsDetails(addOnsDetailsArray);
		} else {
			setAddOnsCheckedNames({
				addOnsPlus: addOnsPlus.filter((e: any) => e !== value)
			});
			setAddOnCodesSelected({
				addOnCodesPlus: addOnCodesPlus.filter((e: any) => e.name !== value)
			});

			const addOnsDetailsArray = [...addOnsDetails];
			addOnsDetailsArray[position]['checked'] = checked;
			addOnsDetailsArray[position]['freeTrialExpired'] = false;
			setAddOnsDetails(addOnsDetailsArray);
		}
		const updatedCheckedState = addOnsCheckedState.map((item, index) =>
			index === position ? !item : item
		);
		setAddOnsCheckedState(updatedCheckedState);
		let totalAddonPrice: any = 0.0;
		let totalFreeTrial: any = 0;
		addOnsDetails.map(function (item: any) {
			if (item.checked === true) {
				totalAddonPrice += parseFloat(item.price.substring(1).replace(/,/g, ''));

				return totalAddonPrice;
			}
		});
		setTotal(totalAddonPrice);
		addOnsDetails.map(function (item: any) {
			if (item.checked === true && item.freeTrialDays > 0) {
				totalFreeTrial += parseFloat(item.price.substring(1).replace(/,/g, ''));
				return totalFreeTrial;
			}
		});
		setFreeTrialTotal(totalFreeTrial);
		setDueToday(true);
		handlePreviewSubscription(planDetails.planCode, addOnsDetails);
	};

	let grandTotal: any = 0;
	let grandStartingTotal: any = 0;
	let billingInterval: any = '';

	if (Object.keys(planDetails).length > 0 && planDetails.planCode !== 'cancel') {
		billingInterval = planDetails.billingInterval;
		const basePlanPrice = parseFloat(planDetails.price.substring(1).replace(/,/g, ''));
		const addOnsTotal = total;
		grandTotal = basePlanPrice + addOnsTotal - freeTrialTotal;
		grandStartingTotal = basePlanPrice + addOnsTotal;
	}
	const defaultAddOns = subscriptionDetail.addOns ? subscriptionDetail.addOns.toArray() : [];
	const filterAddOnsName = addOnsDetails.filter((e: any) => e.checked === true);
	const addOnsCheckedItems = filterAddOnsName.map((item: any) => item.name).join(', ');

	const handleFocus = () => {
		if (passwordRef.current) {
			passwordRef.current.focus();
			passwordRef.current.classList.add('focus');
		}
	};
	//Re-authentication
	const handleChange = (event: any) => {
		addClasses();
		const { name, value } = event.target;

		setSignInForm({ ...signInForm, [name]: value });

		if (name === 'password') {
			formErrors.passwordErrorMessage = '';
			formErrors.isPasswordFieldValid = true;
			setIsAccountLocked(false);
		}

		setFormErrorMessage(formErrors);

		if (formErrors.isPasswordFieldValid) {
			setIsSignButtonDisabled(false);
		} else {
			setIsSignButtonDisabled(true);
		}
	};
	const handlePasswordBlur = () => {
		if (passwordRef.current) {
			passwordRef.current.classList.remove('active');
			passwordRef.current.classList.remove('focus');
		}
		setIsAccountLocked(false);
		if (signInForm.password) {
			setFormErrorMessage({ ...formErrors, isPasswordFieldValid: true, passwordErrorMessage: '' });
			setIsSignButtonDisabled(false);
		}
	};

	const passwordVisibleHide = () => {
		setIsPasswordVisible(!isPasswordVisible);
	};
	const signInSubmit = (event: any) => {
		event.preventDefault();

		if (passwordRef.current) {
			passwordRef.current.blur();
		}

		if (!signInForm.password) {
			formErrors.isPasswordFieldValid = false;
			formErrors.passwordErrorMessage = 'This is a required field.';
			if (passwordRef.current !== null) {
				passwordRef.current.focus();
			}
		}
		setFormErrorMessage(formErrors);
		if (formErrors.isPasswordFieldValid) {
			if (isEmailChanged) {
				setIsEmailChanged(false);
				checkEmailExits(user.email);
			} else {
				setIsSpinnerLoading(true);
				callLoginAPI(user.email, signInForm.password, 'password');
			}
		} else {
			setIsSignButtonDisabled(true);
		}
	};

	const callLoginAPI = async (
		email: string,
		credentials: string,
		signInType: string,
		socialResponse?: SocialResponse
	) => {
		const captchaToken = await generateRecaptchaToken('vz_account_renew_reauthendication').then(
			(response: any) => response
		);

		const loginParamObject = {
			email: email,
			password: credentials,
			reCaptchaToken: captchaToken
		};

		requestRedwolfService(accountAPI.login(loginParamObject))
			.then((response: any) => {
				if (response.constructor.$metadata$.simpleName === 'Success') {
					handleRenewSubscriptionConfirm();
					transaction && transaction.end();
				} else {
					//TODO:(3) Move this error handling to a common block
					if (
						response.response &&
						(response.response.status === 400 || response.response.status === 401) &&
						signInType === 'password'
					) {
						if (response?.response?.detail === 'ACCOUNT_LOCKED') {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage:
									'Your account is locked for 5 minutes due to too many login attempts.'
							});
							setIsAccountLocked(true);
						} else {
							setFormErrorMessage({
								...formErrors,
								isEmailFieldValid: false,
								isPasswordFieldValid: false,
								passwordErrorMessage: 'Incorrect password entered.'
							});
						}

						setIsSignButtonDisabled(true);
					} else {
						if (response?.response?.detail === 'Unauthorized') {
							navigate('/sign-up');
						} else {
							Sentry.captureException(new Error('login:' + response));
							showTechnicalErrorMessage();
						}
					}
					setIsSpinnerLoading(false);
				}
			})
			.catch((err: any) => {
				showTechnicalErrorMessage();
				transaction && transaction.end();
				setIsSpinnerLoading(false);
				Sentry.captureException(err);
			});
	};

		//TODO:(2) Socila Login is being used in 3 different componets, try to reuse this method
		const callSocialLoginAPI = async (
			email: string,
			credentials: string,
			signInType: string,
			socialResponse?: SocialResponse
		) => {
			const captchaToken = await generateRecaptchaToken('vz_account_social_signin').then(
				(response: any) => response
			);
	
			let loginParamObject = {};
			// TODO:(5) Make this signInType block re-usable
			if (signInType === 'facebook') {
				loginParamObject = {
					email: email,
					facebookToken: credentials,
					googleToken: '',
					appleToken: '',
					reCaptchaToken: captchaToken
				};
			} else if (signInType === 'google') {
				loginParamObject = {
					email: email,
					facebookToken: '',
					googleToken: credentials,
					appleToken: '',
					reCaptchaToken: captchaToken
				};
			} else if (signInType === 'apple') {
				loginParamObject = {
					email: email,
					facebookToken: '',
					googleToken: '',
					appleToken: credentials,
					reCaptchaToken: captchaToken
				};
			}
			requestRedwolfService(accountAPI.socialLogin(loginParamObject))
				.then((response: any) => {
					if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
						handleRenewSubscriptionConfirm();

						// TODO:(4) Make this GA name logic re-usable
						let gaEvent = '';
						if (signInType === 'password') {
							gaEvent = 'Vizio_Account_web_Traditional_SignIn';
						} else if (signInType === 'facebook') {
							gaEvent = 'Vizio_Account_web_FB_SignIn';
						} else if (signInType === 'google') {
							gaEvent = 'Vizio_Account_web_Google_SignIn';
						} else if (signInType === 'apple') {
							gaEvent = 'Vizio_Account_web_Apple_SignIn';
						}
	
						logGoogleAnalyticsEvent(gaEvent, {
							item_title: signInType === 'password' ? 'Traditional' : signInType
						});
	
					} else {
						//TODO:(3) Move this error handling to a common block
						if (
							response.response &&
							(response.response.status === 400 || response.response.status === 401) &&
							signInType === 'password'
						) {
							if (response?.response?.detail === 'ACCOUNT_LOCKED') {
								setFormErrorMessage({
									...formErrors,
									isEmailFieldValid: false,
									isPasswordFieldValid: false,
									passwordErrorMessage:
										'Your account is locked for 5 minutes due to too many login attempts.'
								});
								setIsAccountLocked(true);
							} else {
								setFormErrorMessage({
									...formErrors,
									isEmailFieldValid: false,
									isPasswordFieldValid: false,
									passwordErrorMessage: 'Please enter a valid Email Address and Password'
								});
							}
							setIsSignButtonDisabled(true);
						} else {
							if (response?.response?.detail === 'Unauthorized') {
								navigate('/sign-up');
							} else {
								Sentry.captureException(new Error('login:' + response));
								showTechnicalErrorMessage();
							}
						}
						setIsSpinnerLoading(false);
					}
				})
				.catch((err: any) => {
					showTechnicalErrorMessage();
					transaction && transaction.end();
					setIsSpinnerLoading(false);
					Sentry.captureException(err);
				});
		};
	

	const passwordHideShow = (event: any) => {
		if (event.key === 'Enter') {
			setIsPasswordVisible(!isPasswordVisible);
		}
	};

	const checkEmailExits = async (email: any) => {
		setIsSpinnerLoading(true);
		const captchaToken = await generateRecaptchaToken('vz_account_check_email_exits').then(
			(response: any) => response
		);
		checkEmailIdExits(email, captchaToken)
			.then((isEmailExists: any) => {
				if (isEmailExists) {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'valid' });
					callLoginAPI(user.email, signInForm.password, 'password');
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_EMAIL_CHECK', { item_title: 'invalid' });
					transaction && transaction.end();
					setIsSpinnerLoading(false);
					setIsUnableFindEmail(true);
					setIsSignButtonDisabled(true);
				}
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const socialSignIn = (socialRespose: SocialResponse) => {
		setIsSpinnerLoading(true);
		callSocialLoginAPI(socialRespose.email, socialRespose.token, socialRespose.type, socialRespose);
	};

	const handleLogout = (event: any) => {
		event.preventDefault();
		handleUserLogout('password-reset');
	};

	const handleUpdateConfirmBack = () => {
		setIsRenewConfirmModalOpen(true);
		setIsReAuthenticationModalOpen(false);
	};
	const updateConfirmModalClose = () => {
		setIsReAuthenticationModalOpen(false);
		setIsRenewConfirmModalOpen(false);
		renewSubscriptionModalToggle(false, false, false);
	};
	const handleReAuthenticationConfirm = (event: any) => {
		event.preventDefault();
		setSignInForm({
			email: user && user.email && user.email !== 'null' ? user.email : '',
			password: ''
		});
		setIsReAuthenticationModalOpen(true);
		if (userProfile.signInType === 'TRADITIONAL') {
			setIsSocialButtonDisabled(true);
		}
		setIsRenewConfirmModalOpen(false);
	};

	const plansListDefault = plansList.map((obj: any) => {
		if (obj.selected === true && planDetails.planCode !== obj.planCode) {
			return { ...obj, selected: false };
		}
		return obj;
	});
	const modifiedPlansList = plansListDefault.map((obj: any) => {
		if (obj.planCode === planDetails.planCode) {
			return { ...obj, selected: true };
		}
		return obj;
	});

	const handleTabsChange = (index: any) => {
		setTabIndex(index);
	};

	const handleKeyDown = (event: any) => {
		if (event.key === 'Enter') {
			signInSubmit(event);
		}
	};

	const disableAddons = subscriptionDetail.plan.planCode === planDetails.planCode ? true : false;

	return (
		<>
			{subscriptionDetail &&
				plansList &&
				isRenewModalOpen &&
				!isRenewConfirmModalOpen &&
				!reAuthentication && (
					<Modal
						containerId="renewSubscriptionModal"
						headingImg={subscriptionDetail.plan.imageUrl}
						show={isRenewModalOpen}
						onHide={renewModalClose}
						heading="Resume Subscription"
						overlaycontainerClass="renewSubscription">
						<form className="edit-modal-form fieldn popupForm">
							<div className="plan_details">
								<div className="Plan-content">
									<h3 className="planDetails_heading">Current Plan</h3>
									<p className="planDetails_priceInfo">
										{`${subscriptionDetail.plan.name} `}
										<span className="planDetails_price">{`${subscriptionDetail.total}/${subscriptionDetail.plan.billingInterval}`}</span>
									</p>
									{defaultAddOns && defaultAddOns.length > 0 && `+ ${defaultAddOns.length} Add-Ons`}
									<p>(Ends {subscriptionDetail.billingDate.replace('Exp.', '')})</p>
								</div>
							</div>
							{isSkeletonLoading ? (
								<PlansSkeleton counts={0} />
							) : (
								<div className="subscription-new-plan">
									{Object.keys(planDetails).length > 0 &&
										planDetails.planCode !== subscriptionDetail.plan.planCode && (
											<span>New Plan</span>
										)}
								</div>
							)}
							<Tabs
								className={`tabcontainer ${addOnsDetails.length > 0 ? '' : 'no-planAddon'}`}
								index={tabIndex}
								onChange={handleTabsChange}>
								<TabList>
									<Tab className="tablist">Base Plan</Tab>
									{addOnsDetails.length > 0 && <Tab className="tablist">Add-Ons</Tab>}
								</TabList>
								<TabPanels>
									<TabPanel tabIndex={-1} className="baseplan-container">
										{isSkeletonLoading ? (
											<PlansSkeleton counts={3} />
										) : (
											<fieldset className="subscription-fieldset">
												<legend className="subscription-legend">{`Renew your subscription plan for ${subscriptionDetail.plan.appName}`}</legend>

												{modifiedPlansList.map((plan: any, index: number) => {
													return (
														<div key={index} className="form_field">
															<input
																type="radio"
																checked={plan.selected}
																name="renew_subscription"
																id={`subscription-renew-${index}`}
																value={plan.name}
																onChange={() => choosePlan(plan)}
															/>
															<label htmlFor={`subscription-renew-${index}`}>
																<span className="planname">{`${plan.name} `}</span>

																<span className="pricedetails">
																	{`${plan.price}`}
																	<span aria-label="per">/</span>
																	{`${plan.billingInterval}`}
																</span>
																<div className="check"></div>
															</label>
														</div>
													);
												})}
											</fieldset>
										)}
									</TabPanel>
									<TabPanel tabIndex={-1} className="addon-container">
										<Accordion collapsible multiple role="list" className="accordion-subscription">
											<Accordion
												collapsible
												multiple
												role="list"
												className="accordion-subscription">
												{addOnsDetails.map((addon: any, index: number) => {
													return (
														<AccordionItem role="listitem" className="accordion-list" key={index}>
															<AccordionButton className="accordion-title">
																<div className="accordion-title-info" id="accordion-title-info">
																	<span className="add-on-title">{addon.name}</span>
																	<div
																		className={`add-on-price-info ${
																			addon.freeTrialDays === 0 ? '' : 'free-trial-offer'
																		}`}>
																		<div className="add-price">
																			{addon.freeTrialDays > 0 && !addon.freeTrialExpired && (
																				<span className="free-trial">
																					{addon.freeTrialDays}
																					{addon.freeTrialDays === 1 && ' Day'}{' '}
																					{addon.freeTrialDays > 1 && ' Days'} Free Trial
																				</span>
																			)}
																			<span className="add-on-price">
																				{addon.price}/{billingInterval}
																			</span>
																		</div>
																	</div>
																</div>
															</AccordionButton>
															<div className="form-checkbox CheckBox">
																<input
																	type="checkbox"
																	aria-labelledby="accordion-title-info"
																	id={`accordion-checkbox-${index}`}
																	name={addon.name}
																	value={addon.name}
																	checked={addon.checked}
																	disabled={disableAddons}
																	onChange={(event) => handleOnChangeAddOns(event, index)}
																/>
																<label htmlFor="accordion-checkbox"></label>
															</div>
															<AccordionPanel className="accordion-details">
																<div className="plan-add-on-container">
																	<div className="channel-logs">
																		{addon.imageUrl !== '' && (
																			<ul>
																				<li>
																					<img alt={addon.name} src={addon.imageUrl} />
																				</li>
																			</ul>
																		)}
																	</div>
																	<div className="channel-list">{addon.description}</div>
																</div>
															</AccordionPanel>
														</AccordionItem>
													);
												})}
											</Accordion>
										</Accordion>
									</TabPanel>
								</TabPanels>
							</Tabs>
							<div className="addplan-total-amount">
								{Object.keys(planDetails).length > 0 && planDetails.planCode !== 'cancel' && (
									<div className="bassic-plan-container">
										<p className="addplan-details">
											<span className="baseplan">Base Plan:</span>
											<span className="addon-value">{planDetails.name}</span>
										</p>
										<span className="select-pricedetails">
											{planDetails.price}/{billingInterval}
										</span>
									</div>
								)}
								{addOnsCheckedNames &&
									addOnsCheckedItems.length > 0 &&
									planDetails.planCode !== 'cancel' && (
										<div className="add-on-info-container">
											<span className="addon">+ Add-Ons:</span>
											<div className="add-on-info-details">
												{/* Start Looping */}

												{filterAddOnsName.map((addonsItem: any, index: number) => {
													return (
														<div className="add-on-block" key={index}>
															<p className="addplan-details">
																<span className="addon-value">{addonsItem.name}</span>
															</p>
															<div className="add-on-offer-info">
																{addonsItem.freeTrialDays > 0 && !addonsItem.freeTrialExpired && (
																	<span className="add-on-offer">
																		({addonsItem.freeTrialDays}
																		{addonsItem.freeTrialDays === 1 && ' Day'}{' '}
																		{addonsItem.freeTrialDays > 1 && ' Days'} Free Trial)
																	</span>
																)}
																<span
																	className={`select-pricedetails ${
																		addonsItem.freeTrialDays > 0 && !addonsItem.freeTrialExpired
																			? 'strike'
																			: ''
																	}`}>
																	{addonsItem.price}/{billingInterval}
																</span>
															</div>
														</div>
													);
												})}

												{/* end Looping */}
											</div>
										</div>
									)}
							</div>

							{isSkeletonLoading ? (
								<PlansSkeleton counts={0} />
							) : (
								<>
									{Object.keys(planDetails).length > 0 &&
										planDetails.planCode !== 'cancel' &&
										dueToday &&
										Object.keys(planPreviewSubscription).length > 0 && (
											<div className="final-total-amount">
												Due Today:
												<span>{planPreviewSubscription.total}</span>
											</div>
										)}

									{Object.keys(planDetails).length > 0 &&
										planDetails.planCode !== 'cancel' &&
										!dueToday && (
											<div className="plan-start-details">
												Subscription Total:
												<span className="due-info">${grandStartingTotal.toFixed(2)}</span>
											</div>
										)}
									{Object.keys(planDetails).length > 0 &&
										planDetails.planCode !== 'cancel' &&
										dueToday &&
										Object.keys(planPreviewSubscription).length > 0 && (
											<div className="plan-start-details">
												Subscription Total:
												<span className="due-info">{planPreviewSubscription.terms}</span>
											</div>
										)}
								</>
							)}

							<div className="button-field-group">
								<button
									type="submit"
									className="primary-button"
									aria-label="Resume Subscription"
									id="renew-subscription-button"
									disabled={Object.keys(planDetails).length === 0}
									onClick={(event) => handleRenewSubscription(event)}>
									Resume Subscription
								</button>
							</div>
						</form>
					</Modal>
				)}

			{subscriptionDetail && planDetails && isRenewConfirmModalOpen && !reAuthentication && (
				<Modal
					containerId="renew-confirm-subscription"
					headingImg={subscriptionDetail.plan.imageUrl}
					show={isRenewConfirmModalOpen}
					onHide={renewConfirmModalClose}
					heading="Resume Subscription"
					containerClasses="renew-subscription"
					overlaycontainerClass="renewSubscription">
					<form className="edit-modal-form fieldn popupForm" id="confirm-renew-subscription">
						<div className="confirm-plan-container">
							<div className="confirm-details-container">
								<div className="confirm-plan-details">
									<div className="new-plan">Selected Plan</div>
									<div className="new-price">
										Billing will resume on {subscriptionDetail.billingDate.replace('Exp.', '')}.
									</div>
								</div>
							</div>
							<div className="addplan-total-amount">
								{Object.keys(planDetails).length > 0 && (
									<div className="bassic-plan-container">
										<p className="addplan-details">
											<span className="baseplan">Base Plan:</span>
											<span className="addon-value">{planDetails.name}</span>
										</p>
										<span className="select-pricedetails">
											{planDetails.price}/{billingInterval}
										</span>
									</div>
								)}
								{addOnsCheckedNames &&
									addOnsCheckedItems.length > 0 &&
									planDetails.planCode !== 'cancel' && (
										<div className="add-on-info-container">
											<span className="addon">+ Add-Ons:</span>
											<div className="add-on-info-details">
												{/* Start Looping */}

												{filterAddOnsName.map((addonItem: any, index: number) => {
													return (
														<div className="add-on-block" key={index}>
															<p className="addplan-details">
																<span className="addon-value">{addonItem.name}</span>
															</p>
															<div className="add-on-offer-info">
																{addonItem.freeTrialDays > 0 && !addonItem.freeTrialExpired && (
																	<span className="add-on-offer">
																		({addonItem.freeTrialDays}
																		{addonItem.freeTrialDays === 1 && ' Day'}{' '}
																		{addonItem.freeTrialDays > 1 && ' Days'} Free Trial)
																	</span>
																)}
																<span
																	className={`select-pricedetails ${
																		addonItem.freeTrialDays > 0 && !addonItem.freeTrialExpired
																			? 'strike'
																			: ''
																	}`}>
																	{addonItem.price}/{billingInterval}
																</span>
															</div>
														</div>
													);
												})}

												{/* end Looping */}
											</div>
										</div>
									)}
							</div>
							{Object.keys(planDetails).length > 0 &&
								planDetails.planCode !== 'cancel' &&
								dueToday &&
								Object.keys(planPreviewSubscription).length && (
									<div className="final-total-amount">
										Due Today:
										<span>{planPreviewSubscription.total}</span>
									</div>
								)}
							{Object.keys(planDetails).length > 0 &&
								planDetails.planCode !== 'cancel' &&
								!dueToday && (
									<div className="plan-start-details">
										Subscription Total:
										<span className="due-info">${grandStartingTotal.toFixed(2)}</span>
									</div>
								)}
							{Object.keys(planDetails).length > 0 &&
								planDetails.planCode !== 'cancel' &&
								dueToday &&
								Object.keys(planPreviewSubscription).length && (
									<div className="plan-start-details">
										Subscription Total:
										<span className="due-info">{planPreviewSubscription.terms}</span>
									</div>
								)}
							{
								<div className="free-trial-terms">
									<div className="title">Subscription Terms</div>
									<div className="free-trial-terms-details">
										This subscription will automatically renew until canceled, which you can do in
										the Subscriptions & Purchases section of your VIZIO Account settings. If you
										cancel this subscription, the cancelation will go into effect at the end of
										your current subscription period. To learn more about your subscriptions, visit{' '}
										<a href="/en/terms/terms-of-service" target="_blank" rel="noreferrer">
											vizio.com/terms
										</a>
										. Change your settings at{' '}
										<a href="/en/account" target="_blank" rel="noreferrer">
											vizio.com/account
										</a>
										.
									</div>
								</div>
							}
						</div>
						<div className="button-field-group m-btn">
							<button type="submit" className="secondary-button" onClick={handleRenewBack}>
								Back
							</button>
							<button
								type="submit"
								className="primary-button"
								onClick={(event) => handleReAuthenticationConfirm(event)}>
								Confirm
							</button>
						</div>
					</form>
				</Modal>
			)}
			{subscriptionDetail && planDetails && reAuthentication && (
				<Modal
					containerId="renew-confirm-subscription"
					headingImg={subscriptionDetail.plan.imageUrl}
					show={reAuthentication}
					onHide={updateConfirmModalClose}
					heading="Resume Subscription"
					containerClasses="renew-subscription"
					overlaycontainerClass="renewSubscription">
					<div className="re-authentication popupForm">
						{socialButtonDisabled ? (
							<div className="subTitle">
								Please re-enter your password to confirm your purchase.
							</div>
						) : (
							<div></div>
						)}
						<div className="login-confirm-container">
							<div className="formContainer">
								<form id="login-form">
									<ul className="form-list">
										<li className="read-only-input">
											{socialButtonDisabled ? <span>{user.email}</span> : <span></span>}
										</li>
										<li className="fields">
											<div
												className={`field ${!formErrors.isPasswordFieldValid ? 'has-error' : ''}`}>
												<div
													className={
														isPasswordVisible
															? 'input-box form-group input-label pw-view'
															: 'input-box form-group input-label'
													}>
													<input
														ref={passwordRef}
														name="password"
														className="form-control password input-text required-entry login-field-transparent"
														id="repass"
														aria-required="true"
														aria-invalid="false"
														aria-describedby="passwordError"
														value={signInForm.password}
														onChange={handleChange}
														onBlur={handlePasswordBlur}
														onClick={(e) => {
															e.preventDefault();
															handleFocus();
														}}
														type={isPasswordVisible ? 'text' : 'password'}
														disabled={!socialButtonDisabled}
														onKeyDown={(event) => handleKeyDown(event)}
													/>
													<label htmlFor="repass" className="control-label">
														Password
													</label>
													<code
														id="passwordError"
														className={`error-red ${isAccountLocked ? 'error_msg' : ''}`}>
														{formErrors.passwordErrorMessage}
													</code>
													<div
														className="input-field-icon"
														tabIndex={0}
														role="button"
														id="passwordToggle"
														aria-label={isPasswordVisible ? 'hide password' : 'show password'}
														onClick={passwordVisibleHide}
														onKeyDown={(event) => passwordHideShow(event)}>
														<em aria-hidden="true" className="icon-eye eye-icon"></em>
													</div>
													<div className="input-field-icon disable">
														<em aria-hidden="true" className="icon-eye eye-icon"></em>
													</div>
												</div>
											</div>
										</li>
									</ul>
									{socialButtonDisabled ? (
										<button onClick={handleLogout} className="link">
											Forgot password?
										</button>
									) : (
										<span className="link-forgot-pass link">Forgot password?</span>
									)}
								</form>
							</div>
							<div className="socialContainer">
								<div className="socialButtons">
									{userProfile.signInType === 'facebook' ? (
										<FacebookSignIn facebookSignIn={socialSignIn} />
									) : (
										<img
											src="./images/buttons-social-facebook.png"
											className="icon-social disabled"
											alt="Facebook login"
										/>
									)}
									{userProfile.signInType === 'google' ? (
										<SignInGoogle googleSignIn={socialSignIn} />
									) : (
										<img
											src="./images/buttons-social-google-web.png"
											className="icon-social disabled"
											alt=""
										/>
									)}
									{userProfile.signInType === 'apple' ? (
										<SignInApple appleSignIn={socialSignIn} />
									) : (
										<img
											src="./images/buttons-social-apple.png"
											className="icon-social social-apple disabled"
											alt="Apple login"
										/>
									)}
								</div>
							</div>
						</div>
						<div className="button-field-group confirm-btn m-btn">
							<button type="submit" onClick={handleUpdateConfirmBack} className="secondary-button">
								Back
							</button>
							{socialButtonDisabled ? (
								<button
									type="submit"
									onClick={signInSubmit}
									disabled={isSignButtonDisabled}
									className="primary-button">
									Confirm
								</button>
							) : (
								<button disabled={true} className="primary-button">
									Confirm
								</button>
							)}
						</div>
					</div>
				</Modal>
			)}
			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default RenewSubscription;
